import { useState, useEffect, useRef, useCallback } from 'react';
import { googleCalendarAPI } from '../services/api';
import logger from '../utils/logger';

// Every connect button in the chat asks at once when the chat loads; share
// one request between them. Only the in-flight request is shared, never a
// stored answer, so coming back after connecting always sees the new calendar.
let inFlight = null;

const fetchIntegrations = () => {
  if (!inFlight) {
    inFlight = googleCalendarAPI.getIntegrations().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
};

/**
 * Whether the signed-in user has a Google Calendar connected.
 * Checked on mount and again whenever the tab becomes visible (e.g. back
 * from connecting in another tab or in the phone's browser).
 *
 * Connected means at least one calendar is still syncing. A calendar with sync
 * off (Google stopped letting us read it) needs a reconnect with the same
 * account — it is never shown as connected.
 *
 * @returns {{ connected: boolean, needsReconnect: boolean, email: string|null }}
 */
const useGoogleCalendarConnection = () => {
  const [connection, setConnection] = useState({ connected: false, needsReconnect: false, email: null });
  const mountedRef = useRef(false);

  const check = useCallback(async () => {
    try {
      const integrations = await fetchIntegrations();
      const list = Array.isArray(integrations) ? integrations : [];
      const working = list.filter(integration => integration.sync_enabled !== false);
      const shown = working.length > 0 ? working : list;
      const primary = shown.find(integration => integration.is_primary) || shown[0];

      if (mountedRef.current) {
        setConnection({
          connected: working.length > 0,
          needsReconnect: working.length === 0 && list.length > 0,
          email: primary?.google_email || null,
        });
      }
    } catch (error) {
      // Not knowing is fine: the user still gets the connect button
      logger.warn('useGoogleCalendarConnection', 'Could not check calendar connection', { message: error.message });
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    check();

    const handleVisibility = () => {
      if (!document.hidden) check();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      mountedRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [check]);

  return connection;
};

export default useGoogleCalendarConnection;
